import React, { useCallback, useEffect, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import styled from 'styled-components';

import BuilderLogo from '~/builder/components/header/BuilderLogo';
import { CioOverlayNoResultsFoundTitle } from '~/searchCio/components/sharedComponents/productComponents';
import useCioAutosuggest from '~/searchCio/hooks/useCioAutosuggest';
import { constructorTextStandard } from '~/searchCio/utils/styles';
import featureNames from '~/shared/constants/featureFlags';
import {
  getBrandValue,
  isYitty as isYittyBrand,
} from '~/shared/utils/brandNameHelper';
import { useCioClient } from '~/shared/utils/useCioClient';
import useHasFeature from '~/shared/utils/useHasFeature';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { Logo } from '~/techstyle-shared/react-builder-models';
import { FormattedMessage, useIntl } from '~/techstyle-shared/react-intl';

import { getCioSuggestionsAndProducts } from '../apis/getCioSuggestionsAndProducts';
import {
  MIN_CHARS_TO_SEARCH,
  DESKTOP_PRODUCT_COUNT,
  AUTOCOMPLETE_API_STALE_TIME,
} from '../constants/cioConstants';
import { productCardsDesktopParams } from '../constants/skeletonLoaderParams';

import {
  FeaturedProductsSection,
  RecentSearchesSection,
  TopSuggestionsSection,
  TrendingProductsSection,
  TrendingSearchesSection,
} from './sharedSections';
import ProductCardsSkeletonLoader from './sharedSections/ProductCardsSkeletonLoader';
import TextListSkeletonLoaderDesktop from './sharedSections/TextListSkeletonLoaderDesktop';

const OverlayContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.palette.white};
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  width: 100vw;
  z-index: 1;
  display: flex;
  flex-direction: column;
`;

/**
 * re: grid calculation
 * we are using grid-template-columns to define the layout of the grid,
 * including the number of columns in the grid, and the size of the columns.
 *
 * in this case, we are defining the first column to be a minimum of 200px wide, but to extend to 1/6 of the viewport screen (minmax)
 * the second column should take up the remaining space on the grid (1 fr)
 *
 * for a thorough explanation of grid, and properties being used,
 * please see https://developer.mozilla.org/en-US/docs/Web/CSS/grid and child pages
 */

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, calc(100% / 6)) 1fr;
  padding: 40px 36px 48px;
  grid-column-gap: 24px;

  @media (min-width: 1200px) {
    grid-column-gap: 32px;
  }
`;

const LeftCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  grid-column-gap: 0px;
  grid-row-gap: 8px;
  max-height: 400px;
`;

const NoResultsFoundWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const BackgroundContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.palette.black};
  height: 100vh;
  width: 100vw;
  opacity: 0.5;
  top: 0;
  right: 0;
  position: fixed;
`;

const CloseButton = styled.button`
  ${constructorTextStandard}
  position: relative;
  margin: 14px 36px 14px;
  text-decoration: none;
  border-radius: 64px;
  border: 1px solid var(--Greyscale-Shadow-90, #333);
  display: flex;
  padding: 10px 12px 12px; // padding bottom added to visually center text in buton
  justify-content: center;
  align-items: center;
  gap: 10px;
  background: none;
  cursor: pointer;
  &:hover,
  &:focus {
    background-color: ${({ theme }) =>
      theme.colors.searchSuggestIsHighlightedBackground};
  }
`;

const LogoWrapper = styled.div`
  margin-left: 33px;
  margin-top: 0px;
  display: flex;

  ${({ theme }) => theme.searchCio.desktopOverlay.logoWrapper}
`;

const SearchBarLayout = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid
    ${({ theme }) => theme.colors.searchCioBarLayoutBorder};
  height: 57px; // cheat by 1px to prevent the border from jumping by 1px when the overlay is opened

  // The Builder Nav has a major breakpoint at 1439px, we need to use those here to facilitate height changes
  @media (min-width: 1439px) {
    height: 60px;
    padding-bottom: 0px;
  }
`;

const DesktopOverlayLayout = ({ hasError, isLoading, showInitState, data }) => {
  if (hasError) {
    return (
      <GridWrapper>
        <LeftCol>
          <TrendingSearchesSection />
        </LeftCol>
        <TrendingProductsSection />
      </GridWrapper>
    );
  }

  if (isLoading) {
    return (
      <GridWrapper>
        <LeftCol>
          <TextListSkeletonLoaderDesktop />
        </LeftCol>
        <ProductCardsSkeletonLoader {...productCardsDesktopParams} />
      </GridWrapper>
    );
  }

  if (showInitState) {
    return (
      <GridWrapper>
        <LeftCol>
          <RecentSearchesSection />
          <TrendingSearchesSection />
        </LeftCol>
        <TrendingProductsSection />
      </GridWrapper>
    );
  }

  // No results found layout
  if (data?.sections?.Products?.length === 0) {
    return (
      <GridWrapper>
        <LeftCol>
          <RecentSearchesSection />
          <TrendingSearchesSection />
        </LeftCol>
        <NoResultsFoundWrapper>
          <CioOverlayNoResultsFoundTitle data-testid="cnstrc-no-results">
            <FormattedMessage
              id="search_autosuggest.no_results_found_message"
              defaultMessage="No results found for"
            />
            {` "${data?.request?.term}"`}
          </CioOverlayNoResultsFoundTitle>
          <TrendingProductsSection />
        </NoResultsFoundWrapper>
      </GridWrapper>
    );
  }

  // products guarenteed to have results
  // constructor team said data tag for autosuggest should be in parent component
  // that houses both products and searches, as apposed to trending/recommendations
  // which should have their data tag in the respective product/search result sections.
  return (
    <GridWrapper data-cnstrc-autosuggest>
      <LeftCol>
        <TopSuggestionsSection
          query={data?.request?.term}
          suggestions={data?.sections?.['Search Suggestions']}
        />
      </LeftCol>
      <FeaturedProductsSection products={data?.sections?.Products} />
    </GridWrapper>
  );
};

DesktopOverlayLayout.propTypes = {
  data: PropTypes.shape({
    sections: PropTypes.shape({
      'Search Suggestions': PropTypes.array,
      Products: PropTypes.array,
    }),
    request: PropTypes.shape({
      term: PropTypes.string,
    }),
  }),
  hasError: PropTypes.bool,
  isLoading: PropTypes.bool,
  showInitState: PropTypes.bool,
};

const messages = defineMessages({
  closeAutosuggestAriaLabel: {
    id: 'search_autosuggest.close_autosuggest_aria_label',
    defaultMessage: 'close search autosuggest menu',
  },
});

const CioDesktopOverlay = ({ setOverlay, inputRef }) => {
  const { query } = useCioAutosuggest();
  const cioClient = useCioClient();
  const { isVip, isElite } = useMembership();
  const secondarySite = useSecondarySite();
  const intl = useIntl();
  const removeOriginFlag = useHasFeature(featureNames.REMOVE_ORIGIN);

  const dynamicQueryParams = {
    query,
    isVip,
    isElite,
    secondarySite,
    removeOriginFlag,
  };

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['CioSuggestionsAndProducts desktop', dynamicQueryParams],
    queryFn: () =>
      getCioSuggestionsAndProducts({
        cioClient,
        productCount: DESKTOP_PRODUCT_COUNT,
        ...dynamicQueryParams,
      }),
    enabled: query?.length >= MIN_CHARS_TO_SEARCH,
    retry: 1,
    staleTime: AUTOCOMPLETE_API_STALE_TIME,
  });
  const overlayRef = useRef();
  const showInitState = query?.length < MIN_CHARS_TO_SEARCH;

  // Use useCallback to memoize the closeOverlay function
  const closeOverlay = useCallback(
    ({ shouldKeepFocus = false } = {}) => {
      if (shouldKeepFocus) {
        inputRef.current.focus();
      }
      setOverlay(false);
    },
    [inputRef, setOverlay]
  );

  const isYitty = isYittyBrand();

  const type = getBrandValue({
    savage: Logo.TYPES.DEFAULT_FULL_LOGO_SXF,
    yitty: Logo.TYPES.DEFAULT_FULL_LOGO_YT,
    default: Logo.TYPES.DEFAULT_FULL_LOGO_FL,
  });

  /**
   * Handles keyboard navigation within the overlay for focusable elements.
   * - Tab: Moves through focusable elements and closes the overlay if the last element is reached.
   * - ArrowUp/ArrowDown: Cycles through focusable elements, wrapping around when necessary.
   *
   * @param {Event} e - The keyboard event triggered by the user.
   */
  const handleKeyboardOverride = useCallback(
    (e) => {
      // close overlay if ESC is pressed anywhere inside the overlay
      if (e.key === 'Escape') {
        closeOverlay({ shouldKeepFocus: true });
        return;
      }

      // Select all focusable elements (those with a tabindex, excluding tabindex="-1")
      const focusableElements = Array.from(
        overlayRef.current.querySelectorAll('[tabindex]:not([tabindex="-1"])')
      );

      const currentIndex = focusableElements.indexOf(document.activeElement);

      // Early return if Tab is pressed and focus is on the last element
      if (
        e.key === 'Tab' &&
        document.activeElement ===
          focusableElements[focusableElements.length - 1]
      ) {
        closeOverlay();
        return;
      }

      const calculateNextIndex = (direction) => {
        return (
          (currentIndex + direction + focusableElements.length) %
          focusableElements.length
        );
      };

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = calculateNextIndex(1);
        if (focusableElements[nextIndex]) {
          focusableElements[nextIndex].focus();
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevIndex = calculateNextIndex(-1);
        if (focusableElements[prevIndex]) {
          focusableElements[prevIndex].focus();
        }
      }
    },
    [closeOverlay]
  );

  useEffect(() => {
    const overlayNode = overlayRef.current;
    overlayNode.addEventListener('keydown', handleKeyboardOverride);
    return () => {
      overlayNode.removeEventListener('keydown', handleKeyboardOverride);
    };
  }, [handleKeyboardOverride]);

  return (
    <>
      <OverlayContainer ref={overlayRef}>
        <SearchBarLayout>
          <LogoWrapper $isYitty={isYitty}>
            <BuilderLogo type={type} />
          </LogoWrapper>
          <CloseButton
            type="button"
            onClick={closeOverlay}
            aria-label={intl.formatMessage(messages.closeAutosuggestAriaLabel)}
            data-testid="cnstrc-close-button"
          >
            <FormattedMessage
              id="search_autosuggest.cancel_search_button_label"
              defaultMessage="Close"
            />
          </CloseButton>
        </SearchBarLayout>
        <DesktopOverlayLayout
          hasError={error}
          isLoading={isLoading && isFetching}
          showInitState={showInitState}
          data={data}
        />
      </OverlayContainer>
      <BackgroundContainer
        data-testid="cnstrc-background-overlay"
        onClick={closeOverlay}
      />
    </>
  );
};

CioDesktopOverlay.propTypes = {
  inputRef: PropTypes.shape({ current: PropTypes.any }),
  setOverlay: PropTypes.func,
};

export default CioDesktopOverlay;
